import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { withBreakpoints } from "@utils/breakpoints/breakpoints.provider";
import { useIsClientSide } from "@utils/hooks/isClient.hook";
import { SearchComponent } from "@utils/search/@types/searchComponent";

import * as ScrollPositionService from "@services/scrollPosition.service";

import {
    getSearchStatus,
    hasResults,
} from "@redux/modules/value/search/search.selectors";
import { SearchStatus } from "@redux/modules/value/search/search.types";

import { SearchResultsComponent } from "@components/Search/SearchResults/searchResults.component";
import { useInitSearch } from "@components/Search/SearchResults/useInitSearch";
import { useSearchRouting } from "@components/Search/SearchResults/useSearchRouting";

const SEARCH_LOADING_ID = "search__loading";
const SEARCH_NO_RESULTS_ID = "searchResults__noResults";
const FOOTER_ID = "footer";

const SearchLoading = () => {
    const searchStatus = useSelector(getSearchStatus);
    const [hasLoaded, setHasLoaded] = useState<boolean>(false);
    const hasProductResults = useSelector(
        hasResults(SearchComponent.PRODUCT_NUMBER_OF_HITS),
    );
    const hasContentResults = useSelector(
        hasResults(SearchComponent.CONTENT_NUMBER_OF_HITS),
    );

    useEffect(() => {
        const loadingElement = document.getElementById(SEARCH_LOADING_ID);
        const footerElement = document.getElementById(FOOTER_ID);
        const noResultsComponent =
            document.getElementById(SEARCH_NO_RESULTS_ID);

        if (searchStatus === SearchStatus.Loading) {
            footerElement?.classList.add("d-none");
            setHasLoaded(true);
        } else if (hasLoaded) {
            loadingElement?.remove();
            footerElement?.classList.remove("d-none");

            if (hasProductResults || hasContentResults) {
                noResultsComponent?.classList.add("d-none");
            } else {
                noResultsComponent?.classList.remove("d-none");
            }
        }
    }, [searchStatus, hasLoaded, hasProductResults, hasContentResults]);

    return null;
};

type SyncRouteProps = {
    ignoreQuery: boolean;
};

const SyncRoute = ({ ignoreQuery }: SyncRouteProps) => {
    useSearchRouting(ignoreQuery);
    return null;
};

const SaveScrollPosition = () => {
    const handleBeforeUnload = () => {
        ScrollPositionService.pushPosition(
            window.scrollY,
            window.location.href,
        );
    };

    useEffect(() => {
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);

    return null;
};

type InitSearchProps = {
    searchFilters?: string;
    query?: string;
    categoryPagePaths?: Array<string>;
};

export type SearchResultProductTileConfig = {
    showButton: boolean;
    allowInstantAddToCart: boolean;
    showCategoryLink: boolean;
    useInsidePadding: boolean;
    amountOfFeatures: number;
    lazyLoad: boolean;
};

const InitSearch = ({
    searchFilters,
    query,
    categoryPagePaths,
}: InitSearchProps) => {
    useInitSearch({
        searchFilters,
        query,
        categoryPagePaths,
    });
    return null;
};

type SearchResultsProps = {
    searchFilters?: string;
    query?: string;
    productTileConfig?: SearchResultProductTileConfig;
    showFacets?: boolean;
    categoryPagePaths?: string;
};

const SearchResults = withBreakpoints(
    ({
        searchFilters,
        query,
        productTileConfig,
        showFacets = true,
        categoryPagePaths,
    }: SearchResultsProps) => {
        const isClientSide = useIsClientSide();

        return (
            <>
                <SaveScrollPosition />
                <SearchLoading />
                <InitSearch
                    searchFilters={searchFilters}
                    query={query}
                    categoryPagePaths={categoryPagePaths?.split(",")}
                />
                <SearchResultsComponent
                    productTileConfig={productTileConfig}
                    showFacets={showFacets}
                />
                {isClientSide && <SyncRoute ignoreQuery={!!query} />}
            </>
        );
    },
);

export { SearchResults };
